import React, { useCallback, useRef, useState } from 'react';
import { ButtonNewRecord, FlexRow, TableDataState, usePaginatedQueryV8 } from 'motion-components';
import { Card } from 'react-bootstrap';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { gqlGetQuery } from '../graphql';
import { buildWhereFromFilterValues, Filters } from './filters';
import { DataGrid } from './grid';
import { handleExport } from './export-columns';
import { useApolloClient } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserLocationPermissions } from 'shared-pages/hooks';
import { observer } from 'mobx-react-lite';

/* ----------------------------------------------------------- */
/* ------------------------ Page ----------------------------- */
export const ContainersQrCodesGridPage = observer(
  ({
    collectionPointId,
    isGridInForm = false,
  }: {
    collectionPointId?: number;
    isGridInForm?: boolean;
  }) => {
    const { appStore, tableStore } = useRootStore();
    const { handleGraphqlError } = useHandleError('MaterialsGridPage');

    const location = useLocation<any>();
    const history = useHistory();
    const [selected, setSelected] = useState<any>(null);
    const { t } = useTranslation();

    const { canView, canAddNew, canEdit, canDelete } = useUserLocationPermissions();

    const handleAddNew = () => {
      history.push({
        pathname: generatePath(getRouteByName('QrCode'), { id: -1 }),
        state: isGridInForm
          ? { fromCollectionPoint: true, collectionPointId }
          : { fromQrCodesGrid: true },
      });
    };

    /* ------------------- Query ------------------------------- */
    const initOrderBy = 'declaredDate DESC';
    const query = usePaginatedQueryV8(
      gqlGetQuery,
      buildWhereFromFilterValues,
      location.state?.order ?? initOrderBy,
      appStore,
      true,
      { collectionPointId },
      !isGridInForm, // History Replace
      !isGridInForm ? undefined : 'qrcodes_grid',
    );
    const {
      loading,
      data,
      dataCount,
      error,
      initPageIndex,
      filtersChanged,
      gridPageChanged,
      queryState,
      queryParams,
      setQueryParams,
      refetch,
    } = query;

    const client = useApolloClient();
    const gridRef = useRef();

    const hasAccess = appStore.isPlatformOwner;

    if (error) {
      handleGraphqlError('query-get', error);
      return <h3 className="text-center">{error.message}</h3>;
    } else {
      return (
        <Card className="shadow">
          {!isGridInForm && (
            <Card.Header className="d-flex justify-content-between align-items-center">
              <Card.Title className="m-0">{t('QR code Συσκευασιών')}</Card.Title>
              {canAddNew && hasAccess && (
                <ButtonNewRecord onClick={handleAddNew} canInsert={canAddNew} twoLines={false} />
              )}
            </Card.Header>
          )}

          <Card.Body className="nopadding">
            {/* Φίλτρα */}
            {!isGridInForm && (
              <Filters
                onChange={filtersChanged}
                initialValues={queryState?.filterValues ?? {}}
                disable={tableStore.state !== TableDataState.browse}
                isGridInForm={isGridInForm}
              />
            )}

            {/* Grid */}
            <DataGrid
              ref={gridRef}
              loading={loading}
              data={data}
              dataCount={dataCount}
              initPageIndex={initPageIndex}
              onPageChange={gridPageChanged}
              handleExport={() => {
                handleExport(client, queryState, t);
              }}
              initOrderBy={queryParams?.order ?? initOrderBy}
              onOrderChange={(orderBy) => {
                const newQueryParams = { ...queryParams, order: orderBy };
                setQueryParams(newQueryParams);
                if (!isGridInForm) {
                  history.replace({
                    pathname: location.pathname,
                    state: {
                      ...location.state,
                      order: orderBy,
                    },
                  });
                }
              }}
              selected={selected}
              setSelected={setSelected}
              refetch={refetch}
              isGridInForm={isGridInForm}
            />
          </Card.Body>
        </Card>
      );
    }
  },
);

